/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  cancellationPolicyService,
  CancellationPolicyStatus,
  type GetCancellationPoliciesParams,
} from "@src/@api-services/cancellationPolicy";
import type { CancellationPolicyRecord } from "../types";

interface CancellationPoliciesState {
  data: CancellationPolicyRecord[];
  total: number;
  params: GetCancellationPoliciesParams;
  loading: boolean;
  error: string | null;
}

const mapCancellationPolicyRow = (row: any): CancellationPolicyRecord => ({
  id: String(row.id),
  businessId: Number(row.businessId),
  businessName: row.businessName ?? undefined,
  hoursBeforeSlot: Number(row.hoursBeforeSlot ?? 0),
  refundPercentage: Number(row.refundPercentage ?? 0),
  status: row.status || CancellationPolicyStatus.INACTIVE,
  createdAt: row.createdAt,
  updatedAt: row.updatedAt,
  deletedAt: row.deletedAt ?? null,
});

export const getCancellationPolicies = createAsyncThunk(
  "cancellationPolicies/getCancellationPolicies",
  async (params: GetCancellationPoliciesParams, { rejectWithValue }) => {
    try {
      const response = await cancellationPolicyService.getCancellationPolicies(params);
      const payload = response.data?.data;
      const rows = (payload?.data ?? []).map(mapCancellationPolicyRow);
      return {
        params,
        data: rows,
        total: Number(payload?.totalRecord ?? rows.length),
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch cancellation policies"
      );
    }
  }
);

export const createCancellationPolicy = createAsyncThunk(
  "cancellationPolicies/createCancellationPolicy",
  async (data: any, { dispatch, getState, rejectWithValue }) => {
    try {
      const response = await cancellationPolicyService.createCancellationPolicy(data);
      const state = getState() as any;
      await dispatch(getCancellationPolicies(state.cancellationPolicies.params));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create cancellation policy"
      );
    }
  }
);

export const updateCancellationPolicy = createAsyncThunk(
  "cancellationPolicies/updateCancellationPolicy",
  async (
    { id, data }: { id: string; data: any },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const response = await cancellationPolicyService.updateCancellationPolicy(id, data);
      const state = getState() as any;
      await dispatch(getCancellationPolicies(state.cancellationPolicies.params));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update cancellation policy"
      );
    }
  }
);

export const deleteCancellationPolicy = createAsyncThunk(
  "cancellationPolicies/deleteCancellationPolicy",
  async (id: string, { dispatch, getState, rejectWithValue }) => {
    try {
      await cancellationPolicyService.deleteCancellationPolicy(id);
      const state = getState() as any;
      await dispatch(getCancellationPolicies(state.cancellationPolicies.params));
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete cancellation policy"
      );
    }
  }
);

const initialState: CancellationPoliciesState = {
  data: [],
  total: 0,
  params: {},
  loading: false,
  error: null,
};

const cancellationPoliciesSlice = createSlice({
  name: "cancellationPolicies",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCancellationPolicies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCancellationPolicies.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getCancellationPolicies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      });
  },
});

export default cancellationPoliciesSlice.reducer;
